import { InsightsReportsFiltersBar } from "@/components/InsightsReports/InsightsReportsFiltersBar";
import { InsightsReportsTable } from "@/components/InsightsReports/InsightsReportsTable";
import {
  createDefaultInsightsFilters,
  includesRecordingDelayPeriod,
  InsightsReportsFilters,
} from "@/types/insightsReports";
import { useCanViewInsights } from "@/services/AuthService";
import { insightsService } from "@/services/InsightsService";
import { useApi } from "@/hooks/useApi";
import { filtersToQueryParams } from "@/helpers/insightsQueryParams";
import { useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { InsightsSegment } from "@/types/insightsApi";
import { useAppData } from "@/context/AppDataContext";

function InsightsReportsLoginWrapper() {
  const { isLoading } = useAppData();
  const canView = useCanViewInsights();

  if (isLoading)
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-col justify-start gap-3 px-4 pt-3 md:flex-row md:items-center">
          <h1 className="text-2xl font-semibold">Insights & Reports</h1>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );

  if (!canView) return <Navigate to="/pipeline" replace />;

  return <InsightsReports />;
}

function InsightsReports() {
  const [filters, setFilters] = useState<InsightsReportsFilters>(createDefaultInsightsFilters);
  const [committedFilters, setCommittedFilters] = useState<InsightsReportsFilters>(createDefaultInsightsFilters);
  const [hasRun, setHasRun] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState<{ column: keyof InsightsSegment; direction: "asc" | "desc" } | null>(null);

  const queryParams = useMemo(() => filtersToQueryParams(committedFilters), [committedFilters]);

  const handleRunReport = useCallback(() => {
    setHasRun(true);
    setCommittedFilters(filters);
  }, [filters]);

  const handleSort = useCallback(
    (columnKey: keyof InsightsSegment) =>
      setSortBy((prev) => {
        if (prev?.column === columnKey) {
          if (prev.direction === "desc") return null;

          return { column: columnKey, direction: prev.direction === "asc" ? "desc" : "asc" };
        }
        return { column: columnKey, direction: "asc" };
      }),
    []
  );

  const { data: segmentsResult, loading: segmentsLoading } = useApi(
    (signal) => insightsService.getSegments(queryParams, signal),
    [JSON.stringify(queryParams)],
    { skip: !hasRun }
  );

  const { data: filterOptions, loading: filterOptionsLoading } = useApi((signal) =>
    insightsService.getFilterOptions(signal)
  );

  const sortedSegments = useMemo(() => {
    const segments = segmentsResult ?? [];

    if (!sortBy) return segments;

    const sorted = [...segments].sort((a, b) => {
      const aValue = a[sortBy.column];
      const bValue = b[sortBy.column];

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortBy.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortBy.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return sorted;
  }, [segmentsResult, sortBy]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col justify-between gap-3 px-4 pt-3 md:flex-row md:items-center">
        <h1 className="text-2xl font-semibold">Insights & Reports</h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFiltersCollapsed((c) => !c)}
          className="h-7 gap-1 px-2 text-xs"
        >
          {filtersCollapsed ? (
            <>
              <ChevronDown className="size-3.5" />
              Show filters
            </>
          ) : (
            <>
              <ChevronUp className="size-3.5" />
              Hide filters
            </>
          )}
        </Button>
      </div>

      <InsightsReportsFiltersBar
        filters={filters}
        onFiltersChange={setFilters}
        onRunReport={handleRunReport}
        collapsed={filtersCollapsed}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
      />

      {hasRun && includesRecordingDelayPeriod(committedFilters) && (
        <div
          role="alert"
          className="flex shrink-0 items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900"
        >
          <AlertTriangle className="size-4 shrink-0" />
          Recordings for calls from today or yesterday may not be available yet. Recordings can take up to two days to
          process.
        </div>
      )}

      <InsightsReportsTable
        key={JSON.stringify(queryParams)}
        segments={sortedSegments}
        isLoading={segmentsLoading}
        fetchRecordings={insightsService.getTaskRecordings}
        handleSort={handleSort}
        sortBy={sortBy}
      />
    </div>
  );
}

export const InsightsReportsPage = InsightsReportsLoginWrapper;
